import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class PortalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[PortalErrorBoundary]", error, errorInfo);
    supabase.functions.invoke("log-client-error", {
      body: {
        message: error.message,
        stack: error.stack,
        url: window.location.href,
        component: errorInfo.componentStack?.slice(0, 500),
      },
    }).catch(() => {});
  }

  handleGoBack = () => {
    window.location.href = "/portal";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md space-y-4">
            <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="text-xl font-bold text-foreground">Portal Error</h2>
            <p className="text-muted-foreground text-sm">
              Something went wrong in this section. The error has been logged.
            </p>
            <Button onClick={this.handleGoBack} variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Return to Dashboard
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default PortalErrorBoundary;
